import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  buildRevenueMixData,
  formatCurrencyValue,
} from './accountingDashboard';

interface AccountingRevenueCompositionCardProps {
  income: number;
  expenses: number;
  balance: number;
}

export function AccountingRevenueCompositionCard({
  income,
  expenses,
  balance,
}: AccountingRevenueCompositionCardProps) {
  const data = buildRevenueMixData(income, expenses, balance);

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-950">
        Income, expense, and balance
      </h3>
      <p className="mt-1 text-sm text-slate-500">
        A quick composition view of what is being earned, spent, and retained.
      </p>

      <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="h-[220px] w-full lg:w-[220px]">
          {!data.length ? (
            <div className="flex h-full items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
              No composition data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={62}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {data.map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrencyValue(Number(value ?? 0)),
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="flex-1 space-y-3">
          {data.map((item) => (
            <div
              key={item.name}
              className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm font-medium text-slate-700">
                  {item.name}
                </span>
              </div>
              <span className="text-sm font-semibold text-slate-950">
                {formatCurrencyValue(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
